import { useState, useEffect } from "react";
import loLogo from "@/assets/lo-logo.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PrefetchLink } from "@/components/PrefetchLink";
import { Menu, X } from "lucide-react";
import ScrollProgress from "@/components/ScrollProgress";
import { useTranslation } from "@/contexts/LanguageContext";

/* ── Tiny animated EQ bars for Music link (pure CSS) ── */
function MiniEq() {
  return (
    <span className="inline-flex items-end gap-[2px] h-3 ml-1.5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-[2px] rounded-full bg-foreground animate-eq-bar"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutInView, setAboutInView] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const { t } = useTranslation();

  const navLinks = [
    { label: t("nav.about"), href: "/about" },
    { label: t("nav.projects"), href: "/projects" },
    { label: t("nav.caseStudies"), href: "/case-studies" },
    { label: t("nav.blog"), href: "/blog" },
    { label: "AI", href: "/ai" },
    { label: t("nav.contact"), href: "/contact" },
    { label: "Music", href: "/music" },
  ];

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        setScrolled(window.scrollY > 40);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setAboutInView(false);
  }, [location]);

  const isActive = (href: string) => {
    if (href === "#about") return isHome && aboutInView;
    if (href === "/") return isHome;
    return location.pathname.startsWith(href);
  };

  const handleClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("#")) {
      if (isHome) {
        const el = document.querySelector(href);
        el?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/" + href);
      }
    }
  };

  const desktopLinkClasses = (href: string) =>
    `relative text-sm font-medium transition-colors py-1 ${
      isActive(href)
        ? "text-foreground"
        : "text-muted-foreground hover:text-foreground"
    } after:absolute after:left-0 after:bottom-0 after:h-[1.5px] after:bg-foreground after:transition-all after:duration-300 ${
      isActive(href)
        ? "after:w-full"
        : "after:w-0 hover:after:w-full"
    }`;

  const mobileLinkClasses = (href: string) =>
    `text-base transition-colors ${
      isActive(href)
        ? "text-foreground font-medium"
        : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <>
      <ScrollProgress />
      <nav
        className={`fixed top-[33px] left-0 right-0 z-50 transition-all duration-300 animate-slide-down ${
          scrolled
            ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto flex items-center justify-between h-16 px-6">
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <img src={loLogo} alt="LO — Lester Oliver homepage" className="h-7 w-auto dark:invert dark:brightness-200" width="28" height="28" />
          </Link>

          {/* Desktop */}
          <ul className="hidden md:flex items-center gap-8">
            {navLinks.filter(l => l.href !== "/music").map((link) =>
              link.href.startsWith("/") ? (
                <li key={link.label}>
                  <PrefetchLink to={link.href} className={desktopLinkClasses(link.href)}>
                    {link.label}
                  </PrefetchLink>
                </li>
              ) : (
                <li key={link.label}>
                  <a
                    href={isHome ? link.href : `/${link.href}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleClick(link.href);
                    }}
                    className={desktopLinkClasses(link.href)}
                  >
                    {link.label}
                  </a>
                </li>
              )
            )}

            {/* Separator dot */}
            <li aria-hidden className="w-1 h-1 rounded-full bg-muted-foreground/30" />

            {/* Music link with animated EQ */}
            <li>
              <PrefetchLink
                to="/music"
                className={`${desktopLinkClasses("/music")} inline-flex items-center`}
              >
                Music
                <MiniEq />
              </PrefetchLink>
            </li>
          </ul>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-3">
            <button
              className="text-foreground"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border animate-slide-up">
            <ul className="flex flex-col gap-4 px-6 py-6">
              {navLinks.map((link) =>
                link.href.startsWith("/") ? (
                  <li key={link.label}>
                    <PrefetchLink
                      to={link.href}
                      onClick={() => setMobileOpen(false)}
                      className={mobileLinkClasses(link.href)}
                    >
                      {link.label}
                    </PrefetchLink>
                  </li>
                ) : (
                  <li key={link.label}>
                    <a
                      href={isHome ? link.href : `/${link.href}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleClick(link.href);
                      }}
                      className={mobileLinkClasses(link.href)}
                    >
                      {link.label}
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>
        )}
      </nav>
    </>
  );
}
